// Commissions report — commission ledger filtered by date + drop-downs.
// Reps see only their own commissions; admin + finance see everyone and can
// pick a rep (the server enforces it). Drop-down filters (Stephanie
// 2026-10-13): status, client paid, bills in — e.g. "all the bills are in but
// the client hasn't paid". Every figure is the commission engine's own.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { StatusPill } from "@/components/ui/StatusPill";
import {
  ReportShell,
  useReportRange,
} from "@/components/ReportShell";
import { SortHeader } from "@/components/ReportRowsTable";
import { nextSort, sortRows, type SortState } from "@/lib/report-rows";

type CommissionStatus = "estimated" | "earned" | "paid" | "voided";

interface CommissionRow {
  id: string;
  invoiceId: string;
  invoiceNumber: number;
  repId: string;
  repName: string;
  repRole: "client_rep" | "order_rep";
  earnedAmount: string;
  paidAmount: string | null;
  status: CommissionStatus;
  estimatedAt: string | null;
  earnedAt: string | null;
  paidAt: string | null;
  orderNumber: number;
  projectId: string;
  projectName: string;
  clientName: string;
  clientPaid: boolean;
  posTotal: number;
  posBilled: number;
}

interface Report {
  range: { from: string; to: string };
  rows: CommissionRow[];
  totals: {
    estimated: string;
    earned: string;
    paid: string;
  };
  fetchedAt: string;
  seesAllReps?: boolean;
}

const STATUS_OPTIONS: Array<{ value: CommissionStatus | "all"; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "estimated", label: "Estimated (not earned yet)" },
  { value: "earned", label: "Earned (ready to pay)" },
  { value: "paid", label: "Paid" },
];

const CLIENT_PAID_OPTIONS = [
  { value: "", label: "Paid or not" },
  { value: "no", label: "Client hasn't paid" },
  { value: "yes", label: "Client has paid" },
] as const;

const BILLS_OPTIONS = [
  { value: "", label: "Bills in or not" },
  { value: "in", label: "All bills entered" },
  { value: "missing", label: "Bills missing" },
] as const;

const SELECT =
  "h-10 rounded-md border border-rule bg-white px-3 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#63768b]";

function statusTone(s: CommissionStatus): "active" | "info" | "dormant" | "closed" {
  switch (s) {
    case "estimated":
      return "info";
    case "earned":
      return "dormant";
    case "paid":
      return "active";
    case "voided":
      return "closed";
  }
}

function statusLabel(s: CommissionStatus): string {
  return s === "estimated" ? "Estimated" : s === "earned" ? "Earned" : s === "paid" ? "Paid" : "Voided";
}

function billsText(r: CommissionRow): string {
  if (r.posTotal === 0) return "No POs";
  if (r.posBilled >= r.posTotal) return "All in";
  return `${r.posBilled} of ${r.posTotal} POs`;
}

function formatCurrency(amount: string | null): string {
  if (amount === null) return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

type CommissionSortKey = "project" | "rep" | "invoice" | "clientPaid" | "bills" | "status" | "commission";
/** Commission shown on the row: paid amount once paid, else the earned amount. */
const shownCommission = (r: CommissionRow): string => (r.status === "paid" ? (r.paidAmount ?? r.earnedAmount) : r.earnedAmount);
const COMMISSION_SORT: Record<CommissionSortKey, (r: CommissionRow) => string | number> = {
  project: (r) => r.projectName || "",
  rep: (r) => r.repName,
  invoice: (r) => r.invoiceNumber,
  clientPaid: (r) => (r.clientPaid ? 1 : 0),
  bills: (r) => (r.posTotal === 0 ? -1 : r.posBilled / r.posTotal),
  status: (r) => statusLabel(r.status),
  commission: (r) => Number(shownCommission(r)),
};

export function ReportCommissionsPage() {
  const { preset, setPreset, range } = useReportRange();
  const { user } = useAuth();
  const seesAll = user?.roles.some((r) => r === "admin" || r === "finance") ?? false;
  const [statusFilter, setStatusFilter] = useState<CommissionStatus | "all">("all");
  const [clientPaid, setClientPaid] = useState<"" | "yes" | "no">("");
  const [billsFilter, setBillsFilter] = useState<"" | "in" | "missing">("");
  const [repId, setRepId] = useState("");

  // Rep picker for admin / finance only (reps are locked to themselves).
  const peopleQ = useQuery({
    queryKey: ["commissions", "people"],
    queryFn: () =>
      api<{ people: Array<{ userId: string; name: string }> }>("/commissions/people"),
    enabled: seesAll,
    staleTime: 5 * 60 * 1000,
  });

  const queryString = useMemo(() => {
    const params = new URLSearchParams({
      from: range.from,
      to: range.to,
    });
    if (statusFilter !== "all") params.append("status", statusFilter);
    if (clientPaid) params.append("client_paid", clientPaid);
    if (billsFilter) params.append("bills", billsFilter);
    if (seesAll && repId) params.append("rep_id", repId);
    return params.toString();
  }, [range, statusFilter, clientPaid, billsFilter, repId, seesAll]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["reports", "commissions", queryString],
    queryFn: () => api<Report>(`/reports/commissions?${queryString}`),
  });

  const [sort, setSort] = useState<SortState<CommissionSortKey> | null>(null);
  const rows = sort ? sortRows(data?.rows ?? [], COMMISSION_SORT[sort.key], sort.dir) : (data?.rows ?? []);
  const onSort = (k: CommissionSortKey) => setSort((cur) => nextSort(cur, k));
  const showRep = data?.seesAllReps ?? seesAll;

  return (
    <ReportShell
      title={seesAll ? "Commissions" : "My commissions"}
      subtitle={
        seesAll
          ? "Every rep's commissions — filter by rep, status, client payment and bills."
          : "Your commissions — filter by status, client payment and bills."
      }
      preset={preset}
      onPresetChange={setPreset}
      range={range}
      fetchedAt={data?.fetchedAt}
    >
      <div className="mb-6 flex flex-wrap items-end gap-3">
        {seesAll && (
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">Rep</span>
            <select aria-label="Rep" value={repId} onChange={(e) => setRepId(e.target.value)} className={SELECT}>
              <option value="">All reps</option>
              {(peopleQ.data?.people ?? []).map((p) => (
                <option key={p.userId} value={p.userId}>{p.name}</option>
              ))}
            </select>
          </label>
        )}
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">Status</span>
          <select
            aria-label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as CommissionStatus | "all")}
            className={SELECT}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">Client payment</span>
          <select
            aria-label="Client payment"
            value={clientPaid}
            onChange={(e) => setClientPaid(e.target.value as "" | "yes" | "no")}
            className={SELECT}
          >
            {CLIENT_PAID_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">Vendor bills</span>
          <select
            aria-label="Vendor bills"
            value={billsFilter}
            onChange={(e) => setBillsFilter(e.target.value as "" | "in" | "missing")}
            className={SELECT}
          >
            {BILLS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      </div>

      {data && (
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <TotalTile label="Estimated" amount={data.totals.estimated} />
          <TotalTile label="Earned" amount={data.totals.earned} />
          <TotalTile label="Paid" amount={data.totals.paid} />
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-brown-100 bg-white" role="region" aria-label="Commissions" tabIndex={0}>
        {isLoading ? (
          <div className="p-10 text-center text-sm text-brown-300">
            Loading…
          </div>
        ) : isError ? (
          <div className="p-10 text-center text-sm text-rust-600">
            Couldn't load report.
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-brown-300">
            No commissions match those filters.
          </div>
        ) : (
          <table className="w-full min-w-[720px]">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
              <tr>
                <SortHeader label="Project" sortKey="project" sort={sort} onSort={onSort} className="px-5 py-3" />
                {showRep && <SortHeader label="Rep" sortKey="rep" sort={sort} onSort={onSort} className="px-5 py-3" />}
                <SortHeader label="Invoice" sortKey="invoice" sort={sort} onSort={onSort} className="px-5 py-3" />
                <SortHeader label="Client paid" sortKey="clientPaid" sort={sort} onSort={onSort} className="px-5 py-3" />
                <SortHeader label="Vendor bills" sortKey="bills" sort={sort} onSort={onSort} className="px-5 py-3" />
                <SortHeader label="Status" sortKey="status" sort={sort} onSort={onSort} className="px-5 py-3" />
                <SortHeader label="Commission" sortKey="commission" sort={sort} onSort={onSort} align="right" className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-brown-50 text-sm">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-cream-50">
                  <td className="px-5 py-4">
                    <Link
                      to={`/projects/${r.projectId}/commissions`}
                      className="font-semibold text-brown-500 hover:underline"
                    >
                      {r.projectName || "Untitled project"}
                    </Link>
                    <div className="text-xs text-brown-300">{r.clientName}</div>
                  </td>
                  {showRep && (
                    <td className="px-5 py-4">
                      <div className="text-brown-500">{r.repName}</div>
                      <div className="text-xs text-brown-300">
                        {r.repRole === "client_rep" ? "Client rep" : "Order rep"}
                      </div>
                    </td>
                  )}
                  <td className="px-5 py-4 text-brown-400">#{r.invoiceNumber}</td>
                  <td className="px-5 py-4">
                    <StatusPill className="normal-case" tone={r.clientPaid ? "active" : "warn"}>
                      {r.clientPaid ? "Paid" : "Not paid"}
                    </StatusPill>
                  </td>
                  <td className="px-5 py-4">
                    <StatusPill className="normal-case" tone={r.posTotal > 0 && r.posBilled >= r.posTotal ? "active" : "warn"}>
                      {billsText(r)}
                    </StatusPill>
                  </td>
                  <td className="px-5 py-4">
                    <StatusPill tone={statusTone(r.status)}>
                      {statusLabel(r.status)}
                    </StatusPill>
                  </td>
                  <td className="px-5 py-4 text-right font-semibold text-brown-500">
                    {formatCurrency(shownCommission(r))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </ReportShell>
  );
}

function TotalTile({ label, amount }: { label: string; amount: string }) {
  return (
    <div className="rounded-lg border border-brown-100 bg-white p-5">
      <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        {label}
      </div>
      <div className="mt-2 font-display text-2xl font-semibold text-brown-500">
        {formatCurrency(amount)}
      </div>
    </div>
  );
}
